import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from "react-toastify";
import AdjustIcon from '@material-ui/icons/Adjust';
import stockService from '../services/StockService';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      maxWidth: 600,
      width: '95%',
    }
  },
  dialogTitle: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      '&:hover fieldset': {
        borderColor: '#226D68',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#226D68',
      },
    },
  },
  productInfo: {
    backgroundColor: '#f8f9fa',
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(3),
    borderLeft: '4px solid #226D68',
  },
  button: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
  },
  adjustButton: {
    background: 'linear-gradient(135deg, #f26522 0%, #ff8c42 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #e55a1a 0%, #f57c32 100%)',
    },
  },
  cancelButton: {
    borderColor: '#666',
    color: '#666',
    '&:hover': {
      borderColor: '#333',
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
  },
  sectionTitle: {
    fontWeight: 600,
    color: '#226D68',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    fontSize: '1.1rem',
  },
  infoBox: {
    padding: theme.spacing(2),
    borderRadius: 8,
    border: '1px solid',
    marginBottom: theme.spacing(2),
  },
  increaseBox: {
    backgroundColor: '#f0f9ff',
    borderColor: '#b3e5fc',
    color: '#01579b',
  },
  decreaseBox: {
    backgroundColor: '#fff3e0',
    borderColor: '#ffcc80',
    color: '#e65100',
  },
  neutralBox: {
    backgroundColor: '#f5f5f5',
    borderColor: '#e0e0e0',
    color: '#666',
  },
}));

const AjusterStockDialog = ({ open, onClose, product, onAjusterSuccess, auth }) => {
  const classes = useStyles();
  const [nouvelleQuantite, setNouvelleQuantite] = useState('');
  const [nouveauStock, setNouveauStock] = useState('');
  const [motif, setMotif] = useState('');
  const [loading, setLoading] = useState(false);

  // Réinitialiser les champs quand le dialog s'ouvre
  useEffect(() => {
    if (open && product) {
      setNouvelleQuantite(product.qtEntrer || '');
      setNouveauStock(product.stock || '');
      setMotif('');
    }
  }, [open, product]);

  const handleAjuster = async () => {
    if (!nouvelleQuantite || !nouveauStock || !motif.trim()) {
      toast.error("Veuillez remplir tous les champs obligatoires", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    const quantiteNum = parseInt(nouvelleQuantite);
    const stockNum = parseInt(nouveauStock);

    if (isNaN(quantiteNum) || quantiteNum < 0 || isNaN(stockNum) || stockNum < 0) {
      toast.error("La quantité et le stock doivent être des nombres positifs", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    setLoading(true);
    try {
      const ajustementData = {
        codeIdProd: product.codeIdProd,
        nouvelleQuantite: quantiteNum,
        nouveauStock: stockNum,
        motif: motif.trim(),
        speudo: auth.nom || 'Utilisateur'
      };

      // Utiliser le service d'ajustement
      await stockService.ajusterStock(ajustementData);
      
      toast.success("Stock et quantité ajustés avec succès", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      
      onAjusterSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'ajustement:', error);
      toast.error("Erreur lors de l'ajustement du stock", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Calculer les différences
  const diffQuantite = nouvelleQuantite && !isNaN(nouvelleQuantite) ? 
    parseInt(nouvelleQuantite) - (product?.qtEntrer || 0) : 0;
  
  const diffStock = nouveauStock && !isNaN(nouveauStock) ? 
    parseInt(nouveauStock) - (product?.stock || 0) : 0;

  // Déterminer le style des boîtes d'information
  const getInfoBoxStyle = (difference) => {
    if (difference > 0) return `${classes.infoBox} ${classes.increaseBox}`;
    if (difference < 0) return `${classes.infoBox} ${classes.decreaseBox}`;
    return `${classes.infoBox} ${classes.neutralBox}`;
  };

  if (!product) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      className={classes.dialog}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <AdjustIcon />
          Ajuster le Stock et la Quantité
        </Box>
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        {/* Informations du produit */}
        <Box className={classes.productInfo}>
          <Typography variant="h6" gutterBottom>
            {product.nomProd}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Quantité actuelle: <strong>{product.qtEntrer}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Stock actuel: <strong>{product.stock}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Prix: <strong>{product.prixProd} €</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Code: <strong>{product.codeIdProd}</strong>
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Typography className={classes.sectionTitle}>
          Ajustement des Quantités
        </Typography>

        <Grid container spacing={2}>
          {/* Champ quantité */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Nouvelle quantité"
              type="number"
              value={nouvelleQuantite}
              onChange={(e) => setNouvelleQuantite(e.target.value)}
              fullWidth
              variant="outlined"
              className={classes.formField}
              required
              InputProps={{
                inputProps: { 
                  min: 0,
                  step: 1
                }
              }}
              helperText="Quantité totale du produit"
            />
          </Grid>

          {/* Champ stock */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Nouveau stock"
              type="number"
              value={nouveauStock}
              onChange={(e) => setNouveauStock(e.target.value)}
              fullWidth
              variant="outlined"
              className={classes.formField}
              required
              InputProps={{
                inputProps: { 
                  min: 0,
                  step: 1
                }
              }}
              helperText="Stock disponible en magasin"
            />
          </Grid>
        </Grid>

        {/* Affichage des différences */}
        {(diffQuantite !== 0 || diffStock !== 0) && (
          <>
            <Box className={getInfoBoxStyle(diffQuantite)}>
              <Typography variant="body2">
                <strong>Quantité:</strong> {product.qtEntrer || 0} → {nouvelleQuantite} 
                {diffQuantite !== 0 && (
                  <span style={{ fontWeight: 600 }}>
                    {diffQuantite > 0 ? ` (+${diffQuantite})` : ` (${diffQuantite})`}
                  </span>
                )}
              </Typography>
            </Box>

            <Box className={getInfoBoxStyle(diffStock)}>
              <Typography variant="body2">
                <strong>Stock:</strong> {product.stock || 0} → {nouveauStock}
                {diffStock !== 0 && (
                  <span style={{ fontWeight: 600 }}>
                    {diffStock > 0 ? ` (+${diffStock})` : ` (${diffStock})`}
                  </span>
                )}
              </Typography>
            </Box>
          </>
        )}

        <Divider style={{ margin: '16px 0' }} />

        {/* Champ motif */}
        <TextField
          label="Motif de l'ajustement"
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          fullWidth
          variant="outlined"
          className={classes.formField}
          required
          multiline
          rows={3}
          helperText="Ex: Inventaire, Correction d'erreur, Retour client, Perte, etc."
        />

        {/* Avertissement si stock > quantité */}
        {nouveauStock && nouvelleQuantite && parseInt(nouveauStock) > parseInt(nouvelleQuantite) && (
          <Box mt={2} p={2} bgcolor="#fff3e0" borderRadius={1} border="1px solid #ffb74d">
            <Typography variant="body2" color="#e65100">
              ⚠️ <strong>Attention:</strong> Le stock ({nouveauStock}) est supérieur à la quantité totale ({nouvelleQuantite}). 
              Cela peut indiquer une incohérence.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', gap: '8px' }}>
        <Button 
          onClick={handleClose}
          className={classes.cancelButton}
          variant="outlined"
          disabled={loading}
        >
          Annuler
        </Button>
        <Button 
          onClick={handleAjuster}
          className={`${classes.button} ${classes.adjustButton}`}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AdjustIcon />}
        >
          {loading ? 'Ajustement...' : 'Ajuster le Stock'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AjusterStockDialog;